import { Injectable } from "@angular/core";
import type * as Ably from "ably";

export interface RealtimeChannel {
    subscribe(
        eventName: string,
        listener: (message: { data: unknown }) => void,
    ): Promise<void>;
    unsubscribe(eventName?: string): void;
}

export type RealtimeConnectionState =
    | "initialized"
    | "connecting"
    | "connected"
    | "disconnected"
    | "suspended"
    | "closing"
    | "closed"
    | "failed";

export interface RealtimeConnection {
    getChannel(name: string): RealtimeChannel;
    onStateChange(
        listener: (state: RealtimeConnectionState) => void,
    ): () => void;
    close(): void;
}

export type AblyTokenSupplier = () => Promise<Ably.TokenRequest>;

type AblyModule = typeof Ably;

async function loadAbly(): Promise<AblyModule> {
    const loaded: AblyModule & { default?: AblyModule } = await import("ably");
    return loaded.default ?? loaded;
}

@Injectable({ providedIn: "root" })
export class AblyClientFactory {
    async create(
        initialTokenRequest: Ably.TokenRequest,
        tokenSupplier: AblyTokenSupplier,
    ): Promise<RealtimeConnection> {
        const { Realtime } = await loadAbly();
        let initialToken: Ably.TokenRequest | null = initialTokenRequest;
        const client = new Realtime({
            authCallback: (_tokenParams, callback) => {
                const token = initialToken
                    ? Promise.resolve(initialToken)
                    : tokenSupplier();
                initialToken = null;
                token
                    .then((tokenRequest) => callback(null, tokenRequest))
                    .catch((err: unknown) =>
                        callback(
                            err instanceof Error ? err.message : String(err),
                            null,
                        ),
                    );
            },
        });
        return {
            getChannel: (name) => {
                const channel = client.channels.get(name);
                return {
                    subscribe: async (eventName, listener) => {
                        await channel.subscribe(eventName, (message) =>
                            listener({ data: message.data }),
                        );
                    },
                    unsubscribe: (eventName) =>
                        eventName
                            ? channel.unsubscribe(eventName)
                            : channel.unsubscribe(),
                };
            },
            onStateChange: (listener) => {
                const callback = (change: Ably.ConnectionStateChange) =>
                    listener(change.current);
                client.connection.on(callback);
                return () => client.connection.off(callback);
            },
            close: () => client.close(),
        };
    }
}
